import {
  Alert,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  NativeSelect,
  Select,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const OnBoarding_SecurityQuestions = forwardRef((props, ref) => {
  const fetchingRef = useRef(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(
    parseInt(process.env.REACT_APP_SECURITY_QUESTIONS_ANSWER_LIMIT, 10) || 3
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useImperativeHandle(ref, () => ({ validate }));

  useEffect(() => {
    const exhausted = localStorage.getItem("securityQuestionAttemptsExhausted");
    if (exhausted === "true") {
      setAlertMessage(
        "Your attempts have been exhausted. Please contact support at info@finatic.co.za."
      );
      props.setDisableContinue(true);
      setIsLoading(false);
    } else {
      fetchQuestions();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQuestions = async () => {
    if (fetchingRef.current || !props.idNumber) return;
    fetchingRef.current = true;
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/credit-report/security-questions`,
        { idNumber: props.idNumber },
        {
          headers: {
            "x-api-key": process.env.REACT_APP_BACKEND_API_KEY,
          },
        }
      );

      const formatted = response.data.data.map((q, i) => ({
        question: q[`question${i + 1}`],
        answers: q.answers,
        key: `question${i + 1}`,
      }));

      setQuestions(formatted);
      props.setDisableContinue(false);
    } catch (error) {
      console.error("Error fetching security questions:", error);
      setAlertMessage(
        error.response?.data?.message || "Failed to fetch security questions."
      );
      props.setDisableContinue(true);
    } finally {
      setIsLoading(false);
    }
  };

  const validate = () => {
    setAlertMessage("");
    let correct = 0;

    questions.forEach((q, i) => {
      const ans = answers[i];
      if (
        ans &&
        q.answers.some((a) => a.answer === ans && a.correctAnswer === 1)
      ) {
        correct++;
      }
    });

    if (correct < questions.length) {
      setAttemptsLeft((prev) => prev - 1);
      if (attemptsLeft <= 1) {
        localStorage.setItem("securityQuestionAttemptsExhausted", "true");
        setAlertMessage(
          "You have exhausted all attempts. Please contact support at info@finatic.co.za."
        );
        props.setDisableContinue(true);
      } else {
        setAlertMessage(
          `Please answer all security questions correctly. You have ${attemptsLeft} attempt(s) left.`
        );
      }
      return false;
    }

    props.setDisableContinue(false);
    return true;
  };

  const handleChange = (index, event) => {
    setAnswers({
      ...answers,
      [index]: event.target.value,
    });
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  const isDev = process.env.NODE_ENV === "development";

  return (
    <>
      {alertMessage && (
        <Grid item xs={12}>
          <Alert severity="error" sx={{ mb: 1 }}>
            {alertMessage}
          </Alert>
        </Grid>
      )}
      {questions.map((q, i) => (
        <Grid key={i} item xs={12} sx={{ mb: 2 }}>
          <FormControl fullWidth required>
            <div>{q.question}</div>
            {isMobile ? (
              <NativeSelect
                value={answers[i] || ""}
                onChange={(e) => handleChange(i, e)}
                fullWidth
                sx={{
                  mt: 1,
                  height: "56px",
                  padding: "16.5px 14px",
                  borderRadius: 4,
                  border: `1px solid ${theme.palette.divider}`,
                  fontSize: theme.typography.body1.fontSize,
                  "&:focus": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
                inputProps={{
                  name: q.key,
                  id: `native-question-${i}`,
                }}
              >
                <option value="" disabled>
                  Select an option
                </option>
                {q.answers.map((ans, idx) => {
                  const label = isDev && ans.correctAnswer === 1
                    ? `-> ${ans.answer}`
                    : ans.answer;
                  return (
                    <option
                      key={idx}
                      value={ans.answer}
                      style={
                        isDev && ans.correctAnswer === 1
                          ? { backgroundColor: "lightgreen" }
                          : undefined
                      }
                    >
                      {label}
                    </option>
                  );
                })}
              </NativeSelect>
            ) : (
              <Select
                labelId={`question-label-${i}`}
                id={`question-select-${i}`}
                value={answers[i] || ""}
                onChange={(e) => handleChange(i, e)}
                displayEmpty
                fullWidth
                renderValue={(sel) => (sel !== "" ? sel : "Select an option")}
                sx={{ mt: 1 }}
              >
                {q.answers.map((ans, idx) => {
                  const label = isDev && ans.correctAnswer === 1
                    ? `-> ${ans.answer}`
                    : ans.answer;
                  return (
                    <MenuItem
                      key={idx}
                      value={ans.answer}
                      style={
                        isDev && ans.correctAnswer === 1
                          ? { backgroundColor: "lightgreen" }
                          : {}
                      }
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            )}
          </FormControl>
        </Grid>
      ))}
    </>
  );
});

export default OnBoarding_SecurityQuestions;
